import React, { useState, useEffect, ErrorInfo, ReactNode } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import GeneratePage from './pages/GeneratePage';
import ChangelogPage from './pages/ChangelogPage';
import MyPagesPage from './pages/MyPagesPage';
import PricingPage from './pages/PricingPage';
import Header from './components/Header';
import Footer from './components/Footer';
import Icon from './components/Icon';
import AffiliateBanner from './components/AffiliateBanner';

// Props and State types for the ErrorBoundary
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Fix: Define state as a class property to correct TypeScript errors
  state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-[var(--color-text-body)]">
          <Icon name="AlertTriangle" className="w-16 h-16 text-[var(--color-danger)] mb-4" />
          <h1 className="text-2xl font-bold text-[var(--color-text-heading)]">حدث خطأ ما</h1>
          <p className="mt-2">نأسف، لقد واجه التطبيق خطأ غير متوقع. يرجى محاولة تحديث الصفحة.</p>
          {this.state.error && (
            <pre className="mt-4 p-4 bg-[var(--color-surface)] text-[var(--color-danger)] rounded text-left text-sm overflow-auto max-w-2xl w-full border border-[var(--color-border)]">
              {this.state.error.toString()}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            className="mt-6 bg-[var(--color-primary)] text-[var(--color-white)] font-bold py-2 px-6 rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors"
          >
            تحديث الصفحة
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}


const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) return savedTheme;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });
  
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <HashRouter>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        <Header 
          theme={theme}
          setTheme={setTheme}
        />
        <main className="flex-grow">
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/my-pages" element={<MyPagesPage />} />
              <Route path="/generate/:id" element={<GeneratePage />} />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/pricing" element={<PricingPage />} />
            </Routes>
          </ErrorBoundary>
        </main>
        <Footer />
        <AffiliateBanner />
      </div>
    </HashRouter>
  );
};

export default App;